// src/components/chatPolicy/BulkPolicyTester.jsx
// Tester en bloque: elegís un usuario y obtenés:
//  - mode='fromUser' → ¿con quiénes puede iniciar chat este usuario?
//  - mode='toUser'   → ¿quiénes pueden iniciarle chat a este usuario?
import React, { useState } from 'react';
import { Button, Form, Spinner, Tabs, Tab, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import UserTypeahead from './UserTypeahead';
import { testBulk } from '../../services/chatPolicyApi';

export default function BulkPolicyTester({ catalog }) {
    const [user, setUser]     = useState([]);
    const [mode, setMode]     = useState('fromUser');
    const [data, setData]     = useState(null);
    const [loading, setLoad]  = useState(false);
    const [filter, setFilter] = useState('');

    const run = async () => {
        const u = user[0];
        if (!u) { toast.warn('Selecciona un usuario'); return; }
        setLoad(true);
        try {
            const r = await testBulk(u._id, mode, 500);
            setData(r);
        } catch (e) {
            toast.error(e.response?.data?.error || 'Error');
        } finally { setLoad(false); }
    };

    const filtered = (rows) => {
        const q = filter.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(r =>
            (r.name || '').toLowerCase().includes(q) ||
            (r.puesto || '').toLowerCase().includes(q) ||
            (r.unidad || '').toLowerCase().includes(q));
    };

    const renderRow = (u) => (
        <div key={u._id} style={{ padding: '6px 8px', borderBottom: '1px solid var(--c-border)', fontSize: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{u.name}</div>
                    <div style={{ color: 'var(--c-text-4)' }}>
                        {u.puesto || u.role}
                        {u.unidad ? ` · ${u.unidad}` : ''}
                        {u.sucursal ? ` · ${u.sucursal}` : ''}
                    </div>
                </div>
                <div style={{ color: 'var(--c-text-4)', fontSize: 10, maxWidth: 280, textAlign: 'right' }}>
                    {u.reason}
                </div>
            </div>
        </div>
    );

    return (
        <div style={{ background: 'var(--c-card)', border: '1px solid var(--c-border)', borderRadius: 12, padding: 16 }}>
            <h5 style={{ fontWeight: 700, marginBottom: 4 }}>Tester en bloque</h5>
            <p style={{ fontSize: 12, color: 'var(--c-text-4)', marginBottom: 12 }}>
                Verifica con quién puede chatear (o quién puede chatearle) un usuario, según las reglas actuales.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'flex-start', marginBottom: 8 }}>
                <UserTypeahead
                    catalog={catalog}
                    value={user}
                    onChange={(v) => setUser(v.slice(-1))}
                    placeholder="Buscar usuario…"
                />
                <Button size="sm" variant="primary" onClick={run} disabled={loading} style={{ borderRadius: 8 }}>
                    {loading ? <Spinner size="sm" animation="border" /> : 'Probar'}
                </Button>
            </div>

            <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
                <Form.Check type="radio" name="bulkmode" label="¿A quiénes puede iniciar chat?"
                    checked={mode === 'fromUser'} onChange={() => setMode('fromUser')} />
                <Form.Check type="radio" name="bulkmode" label="¿Quiénes pueden iniciarle chat?"
                    checked={mode === 'toUser'} onChange={() => setMode('toUser')} />
            </div>

            {data && (
                <>
                    <div style={{ marginBottom: 10, fontSize: 12, color: 'var(--c-text-4)' }}>
                        Sujeto: <strong>{data.subject?.name}</strong> ({data.subject?.puesto || data.subject?.role}{data.subject?.unidad ? ` · ${data.subject.unidad}` : ''})
                        {data.truncated && <Badge bg="warning" text="dark" style={{ marginLeft: 8 }}>resultados truncados</Badge>}
                    </div>

                    <Form.Control
                        size="sm"
                        placeholder="Filtrar por nombre / puesto / unidad…"
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                        style={{ borderRadius: 8, marginBottom: 8 }}
                    />

                    <Tabs defaultActiveKey="allowed" className="mb-2">
                        <Tab eventKey="allowed" title={<span>Permitidos <Badge bg="success">{data.counts?.allowed ?? 0}</Badge></span>}>
                            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--c-border)', borderRadius: 8 }}>
                                {filtered(data.allowed || []).length === 0
                                    ? <p style={{ padding: 12, fontSize: 12, color: 'var(--c-text-4)', margin: 0 }}>Sin resultados.</p>
                                    : filtered(data.allowed).map(renderRow)}
                            </div>
                        </Tab>
                        <Tab eventKey="denied" title={<span>Bloqueados <Badge bg="danger">{data.counts?.denied ?? 0}</Badge></span>}>
                            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--c-border)', borderRadius: 8 }}>
                                {filtered(data.denied || []).length === 0
                                    ? <p style={{ padding: 12, fontSize: 12, color: 'var(--c-text-4)', margin: 0 }}>Sin resultados.</p>
                                    : filtered(data.denied).map(renderRow)}
                            </div>
                        </Tab>
                    </Tabs>
                </>
            )}
        </div>
    );
}
